export const inputCls =
  "w-full bg-[#070A0F] border border-[#D4AF37]/25 px-4 py-3 text-sm text-[#F7F8F9] placeholder:text-[#94A3B8]/50 focus:border-[#D4AF37] focus:outline-none transition-colors duration-300";

export const Label = ({ children, hint }) => (
  <label className="block text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mb-2">
    {children}
    {hint && <span className="normal-case tracking-normal text-[#94A3B8] ml-2">{hint}</span>}
  </label>
);

export const Field = ({ label, hint, children, value, minLength, showErrors, testId, error }) => {
  const len = (value || "").trim().length;
  const short = minLength && len < minLength;
  const showMsg = short && (showErrors || len > 0);
  return (
    <div>
      <Label hint={hint}>{label}</Label>
      {children}
      {error && showErrors && !value && (
        <p className="text-[11px] text-[#F5B7B1] mt-1.5" aria-live="polite" data-testid={testId ? `${testId}-error` : undefined}>{error}</p>
      )}
      {minLength && (
        <div className="flex items-start justify-between gap-3 mt-1.5" data-testid={testId ? `${testId}-hint` : undefined}>
          <p className={`text-[11px] leading-snug ${showMsg ? "text-[#F5B7B1]" : "text-transparent"}`} aria-live="polite"
            data-testid={testId ? `${testId}-error` : undefined}>
            {showMsg ? `Please tell us a bit more — at least ${minLength} character${minLength === 1 ? "" : "s"}` : "\u00a0"}
          </p>
          <span className={`font-coords text-[10px] tracking-[0.15em] shrink-0 ${short ? "text-[#94A3B8]" : "text-[#E8C15A]"}`}
            data-testid={testId ? `${testId}-counter` : undefined}>
            {len} / {minLength} min
          </span>
        </div>
      )}
    </div>
  );
};

/** Returns true when every [value, minLength] pair meets its minimum (trimmed). */
export const meetsMinimums = (pairs) => pairs.every(([v, min]) => (v || "").trim().length >= min);

export const CheckGroup = ({ label, hint, options, value, onChange, testPrefix }) => {
  const toggle = (opt) =>
    onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
  return (
    <div>
      <Label hint={hint}>{label}</Label>
      <div className="grid sm:grid-cols-2 gap-2" data-testid={`${testPrefix}-group`}>
        {options.map((opt) => {
          const on = value.includes(opt);
          const id = `${testPrefix}-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
          return (
            <button
              type="button"
              key={opt}
              onClick={() => toggle(opt)}
              aria-pressed={on}
              className={`flex items-center gap-3 text-left text-xs px-3 py-2.5 border transition-colors duration-200 ${
                on ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#F5F3EF]" : "border-[#D4AF37]/20 text-[#94A3B8] hover:border-[#D4AF37]/50"
              }`}
              data-testid={id}
            >
              <span className={`h-3.5 w-3.5 shrink-0 border ${on ? "bg-[#D4AF37] border-[#D4AF37]" : "border-[#D4AF37]/50"}`} />
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export const GoldButton = ({ children, className = "", ...props }) => (
  <button
    {...props}
    className={`w-full bg-[#D4AF37] text-[#070A0F] font-display text-sm tracking-[0.25em] py-4 hover:bg-[#F3E08A] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
  >
    {children}
  </button>
);

export const StepBar = ({ steps, current }) => (
  <ol className="flex items-center gap-2 font-coords text-[9px] tracking-[0.25em] uppercase" data-testid="apply-stepbar">
    {steps.map((s, i) => (
      <li key={s} className="flex items-center gap-2">
        <span className={`h-5 w-5 flex items-center justify-center border ${
          i < current ? "bg-[#D4AF37] border-[#D4AF37] text-[#070A0F]" : i === current ? "border-[#D4AF37] text-[#E8C15A]" : "border-[#D4AF37]/25 text-[#94A3B8]/60"
        }`}>{i + 1}</span>
        <span className={i === current ? "text-[#E8C15A]" : "text-[#94A3B8]/70"}>{s}</span>
        {i < steps.length - 1 && <span className="w-6 h-px bg-[#D4AF37]/25" />}
      </li>
    ))}
  </ol>
);

/** Hidden anti-spam field: invisible to people, filled by bots. Backend rejects/drops when non-empty. */
export const Honeypot = ({ value, onChange }) => (
  <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
    <label htmlFor="website-field">Website</label>
    <input id="website-field" name="website" type="text" tabIndex={-1} autoComplete="off" value={value} onChange={onChange} data-testid="honeypot-field" />
  </div>
);

export const PrivacyNote = ({ children }) => (
  <p className="text-[11px] text-[#94A3B8]/80 leading-relaxed" data-testid="privacy-note">
    {children} See our{" "}
    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#E8C15A] hover:text-[#F3E08A] underline underline-offset-2" data-testid="privacy-link">
      Privacy Policy
    </a>.
  </p>
);
